import * as React from 'react';
import styled, {css} from "styled-components";
import {IconLogo} from "../index";
import {useRoute} from "react-router5";
import {RouteName} from "router/routes";
import {transition} from "helpers/css";
import {navigateTo} from "helpers/navigate-to";
import useClickOutside from "helpers/useClickOutside";
import {IconBurgerClose, IconBurgerOpen} from "./icons";

const NavHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const NavContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  transition: max-height .3s, padding-top .3s, padding-bottom .3s;
`;

const Navigation = styled.div`
  display: grid;
  grid-template-columns: repeat(5, max-content);
  gap: 30px;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  letter-spacing: 0.01em;
`;

const NavItem = styled.div<{active?: boolean}>`
  color: ${({active}) => active ? '#3264EF' : 'inherit'};
  cursor: pointer;
  
  &:hover {
    color: ${({active}) => active ? '#3264EF' : '#89898D'};
  }
`
const MenuToggle = styled.div<{active?: boolean}>`
  display: none;
  width: 21px;
  height: 21px;
`

const Buttons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 10px;
  cursor: pointer;
`

const Button = styled.a<{primary?: boolean}>`
  padding: 10px 20px;
  border-radius: 5px;
  background: ${({primary}) => primary 
      ? '#3264EF' 
      : 'transparent'
  };

  border: 
      1px 
      solid 
      ${({primary}) => primary ? 'transparent' : '#E3E7ED'};

  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: 0.01em;
  
  color: ${({primary}) => primary 
      ? 'white' 
      : 'inherit'
  };

  ${transition()};
  
  &:hover {
    ${({primary}) => primary && css`
      box-shadow: 0 2px 10px rgba(50, 100, 239, 0.6);
    `}
    ${({primary}) => !primary && css`
      background: #E5E5E5;
      border-color: transparent;
    `}
  }
`

const Container = styled.div<{isOpened?: boolean}>`
  position: relative;
  z-index: 10;
  display: grid;
  align-items: center;
  grid-template-columns: max-content 1fr;
  gap: 160px;
  max-width: 1160px;
  padding: 12px;
  box-sizing: border-box;
  margin: 0 auto 20px;

  @media screen and (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background-color: white;
    
    ${MenuToggle} {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    ${NavHeader} {
      padding-bottom: 12px;
      border-bottom: 1px solid #EDF0F5;
      box-shadow: 10px 5px 10px rgba(208, 211, 219, 0.1);
    }
    
    ${NavContainer} {
      max-height: 0;
      overflow: hidden;
      display: block;
      position: absolute;
      top: 100%;
      right: 0;
      width: 100%;
      max-width: 320px;
      background-color: white;
      border-radius: 5px;
      padding: 0 10px;
      z-index: -1;
      
      ${({isOpened}) => isOpened && css`
        max-height: 300px;
        padding-top: 10px;
        padding-bottom: 10px;
        z-index: 5;
      `}
      
      ${Navigation} {
        display: flex;
        flex-direction: column;
      }

      ${Buttons} {
        margin-top: 20px;
        display: grid;
        grid-template-columns: 100%;
        gap: 10px;
      }
    }
  }

  @media screen and (max-width: 600px) {
    display: block;
    background-color: white;

    ${({isOpened}) => isOpened && css`
      position: fixed;
      right: 0;
      left: 0;
      height: 65px;
    `}

    ${NavHeader} {
      padding-bottom: 12px;
      border-bottom: 1px solid #EDF0F5;
      box-shadow: 10px 5px 10px rgba(208, 211, 219, 0.1);
    }

    ${NavContainer} {
      display: block;
      position: fixed;
      top: 65px;
      right: 0;
      left: 0;
      bottom: 0;
      height: 100vh;
      width: 100%;
      max-width: none;
      background-color: white;
      border-radius: 5px;
      padding: 0 10px;
      z-index: -1;

      ${({isOpened}) => isOpened && css`
        max-height: 100vh;
        padding-top: 10px;
        padding-bottom: 10px;
        z-index: 5;
      `}

      ${Navigation} {
        display: flex;
        flex-direction: column;
      }

      ${Buttons} {
        margin-top: 20px;
        display: grid;
        grid-template-columns: 100%;
        gap: 10px;
      }
    }
  }
`;

/**
 * render landing page header
 * @constructor
 */
export default function Header(): React.ReactElement {
  const {route} = useRoute();
  const rn: RouteName = route?.name;

  const [isBurgerOpened, setBurgerOpened] = React.useState(false)

  const containerRef = React.createRef<HTMLDivElement>();

  /**
   * The handle click outside of the wrapper.
   */
  useClickOutside(containerRef, () => {
    setBurgerOpened(false);
  });

  function toggleOpened() {
    setBurgerOpened(prevState => !prevState);
  }

  function hideMenuAndNavigate(rn: RouteName) {
    setBurgerOpened(false);
    navigateTo(rn);
  }

  return (
    <Container ref={containerRef} isOpened={isBurgerOpened}>
      <NavHeader>
        <IconLogo />
        <MenuToggle onClick={toggleOpened}>
          {isBurgerOpened
            ? <IconBurgerClose />
            : <IconBurgerOpen />
          }
        </MenuToggle>
      </NavHeader>
      <NavContainer>
        <Navigation>
          <NavItem
            active={rn === 'landing'}
            onClick={() => hideMenuAndNavigate('landing')}
          >
            О платформе
          </NavItem>
          {/*<NavItem*/}
          {/*  active={rn === 'landing.blog'}*/}
          {/*  onClick={() => navigateTo('landing.blog')}*/}
          {/*>*/}
          {/*  Блог*/}
          {/*</NavItem>*/}
          <NavItem
            active={rn === 'landing.about'}
            onClick={() => hideMenuAndNavigate('landing.about')}
          >
            О компании
          </NavItem>
          <NavItem
            active={rn === 'landing.faq'}
            onClick={() => hideMenuAndNavigate('landing.faq')}
          >
            FAQ
          </NavItem>
          <NavItem
            active={rn === 'landing.feedback'}
            onClick={() => hideMenuAndNavigate('landing.feedback')}
          >
            Предложить идею
          </NavItem>
        </Navigation>
        <Buttons>
          <Button primary onClick={() => hideMenuAndNavigate('register')}>
            Зарегистрироваться
          </Button>
          <Button onClick={() => hideMenuAndNavigate('login')}>
            Войти
          </Button>
        </Buttons>
      </NavContainer>
    </Container>
  );
}
